import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Platform, View, StyleSheet } from 'react-native';

import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import { capitalize } from '../../utils/stringFunctions';
import FilledButton from '../FilledButton';
import MyFloatingLabelInput from '../form/MyFloatingLabelInput';
import MyDateOfBirthInput from '../form/MyDateOfBirthInput';

const styles = StyleSheet.create({
  container: {
    padding: Layout.isSmall ? 16 : 0,
    flex: 1,
    flexDirection: 'column',
    backgroundColor: Colors.white,
  },
  topRow: {
    flex: 1,
  },
  buttonRow: {
    ...Layout.formFooter,
  },
  inputField: {
    flex: 1,
    paddingLeft: 0,
  },
  floatingLabel: {
    position: 'absolute',
    top: 0,
    color: Colors.grayText,
    fontSize: 14,
    marginTop: 8,
  },
  leftHalf: {
    marginRight: 16,
  },
  labelTextStyle: {
    position: 'absolute',
    left: '100%',
  },
  buttonText: {
    color: Colors.white,
    fontSize: 14,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  error: {
    marginTop: 10,
    color: Colors.brightRed,
  },
  smallErrorText: {
    fontSize: 12,
    color: Colors.brightRed,
  },
});

function UserInfoForm(props) {
  const { translation, registration, locale, goNext, goBack } = props;
  const [user, setUser] = useState(registration.user);
  const [errors, setErrors] = useState({ birth_date: null });
  const year2000 = 946750000;
  const labels = {
    first_name: capitalize(translation.app_personal_edittext_firstname),
    last_name: capitalize(translation.app_personal_edittext_lastname),
    next: translation.fragment_registration_family_button_next,
    back: translation.fragment_registration_family_button_back,
    zip_code: translation.app_personal_edittext_zipcode,
    birth_date: translation.app_personal_edittext_dob,
  };

  // should return null if there are no errors
  const validateError = (field, value) => {
    switch (field) {
      case 'birth_date':
        // all integers are valid as it is unix epoch time
        if (Number.isInteger(value)) {
          return null;
        }

        if (!value) {
          return Platform.OS == 'web' ? labels.birth_date : null;
        }

        if (!Number.isInteger(value) || value == year2000) {
          return `${invalid} ${translation.app_personal_edittext_dob}`;
        }
        return null;
      case 'first_name':
      case 'last_name':
        if (value && value !== '' && value.length > 0) {
          return null;
        } else {
          return labels[field];
        }
      case 'zip_code':
        if (value && value.length === 5) {
          return null;
        } else {
          return labels[field];
        }
      default:
        return null;
    }
  };

  const updateBirthdate = (value) => {
    const invalid = translation.app_invalid_error;
    const newUser = Object.assign({}, user);
    const newErrors = Object.assign({}, errors);
    if (validateError('birth_date', value)) {
      newErrors.birth_date = `${invalid} ${translation.app_personal_edittext_dob}`;
    } else {
      newUser.birth_date = value;
      newErrors.birth_date = null;
    }
    setErrors(newErrors);
    setUser(newUser);
  };

  const setChanges = (field, value) => {
    const newUser = Object.assign({}, user);
    const newErrors = Object.assign({}, errors);
    newErrors[field] = validateError(field, value);
    newUser[field] = value;
    setErrors(newErrors);
    setUser(newUser);
  };

  const renderInputElement = (key) => {
    let value = user[key];
    if (key === 'zip_code' && value) {
      value = value.replace(/\D/g, '');
    }

    if (value || value == 0) value = value.toString();
    let label = null;
    label = labels[key];

    if (key == 'birth_date') {
      return (
        <MyDateOfBirthInput
          translation={translation}
          onSelect={updateBirthdate}
          date={value}
          label={label}
          locale={locale}
          width={Layout.isSmall ? 328 : 440}
          error={errors[key] || null}
          onboarding
        />
      );
    }

    return (
      <MyFloatingLabelInput
        testID={`UserInfoForm-${key}`}
        label={label}
        value={value || ''}
        onChangeText={(text) => {
          setChanges(key, text);
        }}
        width={Layout.isSmall ? 328 : 440}
        error={errors[key] || ''}
      />
    );
  };

  const invalid = translation.app_invalid_error;

  const submitForm = () => {
    const fields = ['birth_date', 'first_name', 'last_name', 'zip_code'];
    let formOk = true;
    const newErrors = Object.assign({}, errors);
    fields.forEach((field) => {
      if (validateError(field, user[field])) {
        formOk = false;
        newErrors[field] = `${invalid} ${labels[field]}`;
      }
    });
    if (formOk) {
      return goNext(user);
    }
    setErrors(newErrors);
  };

  let allowSubmit = true;
  if (Platform.OS === 'web') {
    if (
      errors.birth_date ||
      errors.first_name ||
      errors.last_name ||
      errors.zip_code ||
      !user.first_name ||
      !user.last_name ||
      !Number.isInteger(user.birth_date) ||
      !user.zip_code
    ) {
      allowSubmit = false;
    }
  } else {
    if (
      errors.birth_date ||
      errors.first_name ||
      errors.last_name ||
      errors.zip_code ||
      !user.first_name ||
      !user.last_name ||
      !user.zip_code
    ) {
      allowSubmit = false;
    }
  }

  return (
    <View style={styles.container} testID="user-details">
      <View style={styles.topRow}>
        {renderInputElement('first_name')}
        {renderInputElement('last_name')}
        {renderInputElement('birth_date')}
        {renderInputElement('zip_code', 'number-pad')}
      </View>

      <View style={styles.buttonRow}>
        <View style={[styles.inputField, styles.leftHalf]}>
          <FilledButton
            onPress={goBack}
            label={labels.back}
            color={Colors.darkGreen}
            full={false}
            testID="button-cancel"
          />
        </View>
        <View style={styles.inputField}>
          <FilledButton
            onPress={submitForm}
            label={labels.next}
            color={Colors.darkGreen}
            full
            testID="button-next"
            disabled={!allowSubmit}
          />
        </View>
      </View>
    </View>
  );
}

UserInfoForm.propTypes = {
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  locale: PropTypes.string.isRequired,
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
  goNext: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
};

export default UserInfoForm;
